// SPDX-License-Identifier: GPL-3.0-or-later
//
// Per-application file detection. Walks the app's folder under the root,
// lists candidate files (CV-eligible extensions), and matches them against
// the user-configured cv/motivation patterns.

import type { Application, ManifestConfig } from '../types';

const DOC_EXTS = new Set(['.pdf', '.docx', '.doc', '.odt', '.txt', '.rtf']);

function lower(s: string): string {
  return s.toLowerCase();
}

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? lower(name.slice(i)) : '';
}

function matchesPattern(filename: string, patterns: readonly string[]): boolean {
  const f = lower(filename);
  return patterns.some((p) => {
    // Patterns like `*needle*` are matched as case-insensitive substring.
    const needle = lower(p.replace(/\*/g, ''));
    return needle.length > 0 && f.includes(needle);
  });
}

export interface AppFiles {
  cv: string | null;
  motivation: string | null;
  all: string[];
}

async function listFiles(dir: FileSystemDirectoryHandle): Promise<string[]> {
  const names: string[] = [];
  for await (const [name, handle] of dir.entries()) {
    if (handle.kind !== 'file') continue;
    if (!DOC_EXTS.has(extOf(name))) continue;
    names.push(name);
  }
  return names.sort((a, b) => a.localeCompare(b));
}

async function resolveFolder(
  root: FileSystemDirectoryHandle,
  relPath: string,
): Promise<FileSystemDirectoryHandle | null> {
  if (!relPath) return null;
  const parts = relPath.split('/').filter(Boolean);
  let cur: FileSystemDirectoryHandle = root;
  for (const part of parts) {
    try {
      cur = await cur.getDirectoryHandle(part);
    } catch {
      return null;
    }
  }
  return cur;
}

export async function detectFiles(
  root: FileSystemDirectoryHandle,
  app: Application,
  config: ManifestConfig,
): Promise<AppFiles> {
  const folder = await resolveFolder(root, app.folderRelPath);
  if (!folder) return { cv: null, motivation: null, all: [] };
  const all = await listFiles(folder);
  const cv = all.find((f) => matchesPattern(f, config.cvPatterns)) ?? null;
  const motivation = all.find((f) => matchesPattern(f, config.motivationPatterns)) ?? null;
  return { cv, motivation, all };
}

// Open a file in a new tab via a blob URL. Returned cleanup revokes the URL.
export async function openFile(
  root: FileSystemDirectoryHandle,
  app: Application,
  filename: string,
): Promise<() => void> {
  const folder = await resolveFolder(root, app.folderRelPath);
  if (!folder) throw new Error('Folder not found');
  const handle = await folder.getFileHandle(filename);
  const file = await handle.getFile();
  const url = URL.createObjectURL(file);
  window.open(url, '_blank', 'noopener,noreferrer');
  // Revoke after a delay — opening in a new tab is async, the new tab needs
  // time to read the blob URL before we revoke it.
  const timer = window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return () => {
    window.clearTimeout(timer);
    URL.revokeObjectURL(url);
  };
}
